import { useDispatch, useSelector } from "react-redux";
import React, { CSSProperties, useEffect } from "react";
import Switch from "common/Buttons/Switch";
import styled, { useTheme } from "styled-components";
import { selectIsDarkMode } from "@shared/slices/common.slice";
import MoonFilled from "@ant-design/icons/MoonFilled";
import SunFilled from "@ant-design/icons/SunFilled";
import { Moon, Sun } from "lucide-react";
import LookIcon from "common/Icons/LookIcon.tsx";
import Flex from "common/Boxes/Flex.tsx";
import { themeUpdated } from "services/webview/webViewBridgeUtils.ts";
import { sharedLib } from "@shared/services/shared.service.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { DARK_MODE_TOGGLE_ID } from "testIds";
import RowLabel from "common/Home/Sider/RowLabel.tsx";

interface DarkModeToggleProps {
  textStyle?: CSSProperties;
  /** New look menu rows: a leading icon. */
  icon?: React.ReactNode;
}

const DarkModeToggle = ({ textStyle, icon }: DarkModeToggleProps) => {
  const dispatch = useDispatch();
  const isDarkMode = useSelector(selectIsDarkMode);
  const appTheme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);

  useEffect(() => {
    themeUpdated(isDarkMode).catch(() => {});
  }, [isDarkMode]);

  const handleToggleDarkMode = () => {
    const updatedIsDarkMode = !isDarkMode;
    dispatch(sharedLib.slices.common.actions.setIsDarkMode(updatedIsDarkMode));
  };

  return (
    <Flex style={{ width: "100%" }}>
      <RowLabel icon={icon} textStyle={textStyle}>
        {t("user_info.dark_mode_label")}
      </RowLabel>
      <StyledSwitch
        data-testid={DARK_MODE_TOGGLE_ID}
        aria-label={t("user_info.dark_mode_label")}
        checked={isDarkMode}
        checkedChildren={
          <LookIcon
            classic={
              <SunFilled style={{ color: appTheme.colors.buttonText }} />
            }
            newLook={
              <Sun
                size={12}
                strokeWidth={2}
                color={appTheme.colors.buttonText}
              />
            }
          />
        }
        onClick={handleToggleDarkMode}
        unCheckedChildren={
          <LookIcon
            classic={
              <MoonFilled style={{ color: appTheme.colors.buttonText }} />
            }
            newLook={
              <Moon
                size={12}
                strokeWidth={2}
                color={appTheme.colors.buttonText}
              />
            }
          />
        }
      />
    </Flex>
  );
};

export default DarkModeToggle;

const StyledSwitch = styled(Switch)`
  .ant-switch-inner-checked {
    i::before {
      background-color: transparent !important;
    }
  }
`;
